// The held-round park (#6341): when the ONE follow-up round a decision would mint
// behind is still `pending` past the CI ceiling, the decision becomes
// `escalate(followup_held | followup_unplaceable)` HERE, in the pure layer — not at
// the executor's mint. Until #6341 the executor noticed the stalled round at mint
// time (`queuedPastCeiling`, `apps/worker` `followup-executors.ts`); the park moved
// up so the VERDICT a hop acts on already carries it, as `decision.behind`.
//
// WHERE IT RUNS (the order is the contract): POST-refresh, over the decision the base
// refresh left standing — a fix round the refresh replaced with a `wait` and an
// update-branch is a refresh, never a park — after the SECOND `rescueStuckPr` pass in
// `gradeHop` (that pass may still replace the refresh's escalation with a rescue ROUND
// this park must then hold), and NO LONGER LAST: since #6344 `gradeHop` runs a THIRD
// `rescueStuckPr` pass over the park's output, because the held-round refile reads the
// park's own `behind` — `followup_held` is the one reason whose round IS the held row
// the park named, so a refile pass running BEFORE the park would see the raw mint,
// never the park's escalation, and could not re-file it. The mint's own in-flight guard stays:
// it re-reads the row fresh at mint time and now only DEDUPES — a round that is
// claimed, or younger than the ceiling, is waited on, never escalated, from there.
//
// THE CEILING ANSWERS FIRST — the executor's order, kept. A decision past its
// structural attempt ceiling is the `wait` the executor mints behind (`wait`, never
// a page), so `parkBehindInFlight` defers to `overAttemptCeiling` before it reads
// the round. That predicate moved here with the park for that one call: one
// implementation, no twin to drift. A stuck-PR RESCUE round (#6172) is exempt from
// its ceiling but NOT from this park — the rescue bought a round, not a bypass of
// the queue ahead of it.

import type { TaskId } from '../../value-objects/ids';
import { CI_WAIT_CEILING_MS } from '../ci-wait';
import { TaskHoldReason } from '../task/hold';
import { holdMeansNoMachine } from '../task/hold-machine';
import { TaskStatus } from '../task/status';
import type { BabysitAction, BabysitBookkeeping, BabysitDecision } from './action';
import { isFeedbackAction } from './action';
import { type BabysitPolicy, DEFAULT_MAX_FEEDBACK_ATTEMPTS } from './policy';

/**
 * The attempt a follow-up's BUDGET reads — the rung of its ladder the ceiling below
 * re-checks. For every action but the FEEDBACK ones it is also the id rung
 * (`mintRungFor`, `apps/worker`).
 *
 * The two self-fix loops carry their attempt on the action, counted by the decision
 * core. The FEEDBACK actions do not: their rung is how many follow-ups for this head
 * already went terminal without landing. It used to be a hard-coded 1, which made the
 * first such terminal outcome permanent — the id was spent, the driver re-decided the
 * same action every sweep, the insert collided with the corpse, and the PR was pinned at
 * that head with its feedback unaddressed forever (tesote/tesote.ai#1919, and #1876
 * before it).
 */
export function attemptFor(action: BabysitAction, book: BabysitBookkeeping): number {
  // #4289: `resolve_conflict`'s `action.attempt` is now the per-head MINT rung
  // (`book.conflictResolveMintAttempts + 1`, `decide.ts`) — monotonic and id-safety
  // only, never the budget count. See `overAttemptCeiling` below for why the
  // structural stop cannot reuse this number the way `fix_pr`'s can.
  if (action.kind === 'fix_pr' || action.kind === 'resolve_conflict') return action.attempt;
  if (isFeedbackAction(action.kind)) return book.feedbackAttempts[action.kind] + 1;
  return 1;
}

/**
 * The per-action attempt ceiling — the structural stop mirroring the decision's
 * budget (`fix_pr` is a bounded loop; the feedback actions climb a per-head ladder).
 * `resolve_conflict` is NOT read through this — see `overAttemptCeiling`.
 */
export function attemptCeilingFor(
  action: BabysitAction,
  policy: BabysitPolicy,
  managed: boolean,
): number {
  // A `fix_pr` round is on the CI ladder when it carries a failed check, and on the
  // per-head feedback ladder when it carries findings alone (#3883). On an AI-managed PR
  // the CI ladder has no count budget (owner ruling 2026-10-09; the decision's convergence
  // stop bounds it), so there is no ceiling here to re-assert.
  if (action.kind === 'fix_pr') {
    if (action.failedChecks.length === 0) return DEFAULT_MAX_FEEDBACK_ATTEMPTS;
    return managed ? Number.POSITIVE_INFINITY : policy.maxCiFixAttempts;
  }
  // The feedback ladder. Past its top the mint stops and the hop records `wait` — the
  // same structural stop the two self-fix loops get, rather than the silent permanent
  // pin a spent id used to produce. The PR is still bounded above by `maxSweeps`.
  if (isFeedbackAction(action.kind)) return DEFAULT_MAX_FEEDBACK_ATTEMPTS;
  return 1;
}

/**
 * Is this mint past its structural ceiling — the hard stop re-asserting the
 * decision's own budget, in case `decideBabysit` and the executor ever disagree?
 *
 * `resolve_conflict` is a DELIBERATE SPECIAL CASE (#4289). Every other action's
 * `attemptFor` value IS its own budget count, so comparing it against
 * `attemptCeilingFor`'s number is a valid re-check. `resolve_conflict`'s `attempt` is
 * now the per-head MINT rung (id-safety only) and can legitimately exceed
 * `maxConflictResolveAttempts` while real budget remains — a base move or an
 * uncharged prior attempt both lower `book.conflictResolveAttempts` without lowering
 * the mint rung. Comparing the MINT rung against the budget ceiling here would
 * re-introduce, one layer down, the exact bug #4289 fixes in the decision core: a PR
 * with genuine budget left would be told `wait` (and never escalated, since
 * `decideBabysit` already said to mint) the moment its head's mint rung — not its
 * charged-attempt count — passed 3. So this reasserts the decision's OWN gate
 * directly off `BabysitBookkeeping.conflictResolveAttempts`, never off `attempt`.
 */
export function overAttemptCeiling(
  action: BabysitAction,
  attempt: number,
  book: BabysitBookkeeping,
  policy: BabysitPolicy,
  managed: boolean,
): boolean {
  // A stuck-PR RESCUE round (#6172, `rescueStuckPr`) is the second deliberate exception: it
  // exists BECAUSE the ladder ran out (a `merge_conflict` park is a spent conflict budget
  // by definition), and its own bound is once per (PR, reason) since the re-arm. Re-asserting
  // the spent budget here would turn every rescue into a `wait` hop that mints nothing and
  // tells nobody, with the escalation it replaced never re-filed.
  //
  // The exemption names the two self-fix loops because only THEY arrive here rescued AND
  // over budget, through exactly these paths:
  // - `resolve_conflict`: a `merge_conflict` rescue round is minted on the MINT rung
  //   (`conflictRound`, `./stuck-rescue.ts`, #4289) and its park means the conflict
  //   budget is spent by definition.
  // - `fix_pr` FINDINGS-ONLY (#6345): the rescue reasons `findings_not_converging`,
  //   `own_review_hold_stale`, `findings_unresolved` and `max_sweeps_exceeded:findings`
  //   buy their round at rung `findingsFixAttempts + 1` (`findingsRound`), and a
  //   findings-only round is on the FEEDBACK ladder here (`attemptCeilingFor`), not the
  //   CI one. The rungs that ESCALATE those reasons are not all past that ladder. Past
  //   it by construction: the per-head rungs, gated `findingsFixAttempts >=
  //   DEFAULT_MAX_FEEDBACK_ATTEMPTS` (`decideManagedFindings`' open-threads arm and
  //   `holdManagedChangesRequested`' spent-rung arm in `./managed-holds.ts`, plus the
  //   one managed-reachable `findings_unresolved` arm — `./decide.ts`'s summary branch
  //   when someone else also holds). At ANY `findingsFixAttempts`: the decline-awaiting
  //   rung (`decideManagedFindings`, zero open threads), the convergence stop (counted
  //   in MOVED rounds across heads, never this head's attempts) and the two sweep
  //   ceilings (`sweepsSoFar`, `findingsFixUnchargedRounds`) — so their rescue round
  //   can mint UNDER this ceiling, where the exemption merely agrees with the general
  //   branch below. `mintPrescribed` (`apps/worker`) re-runs THIS predicate over the
  //   rescued round: without the `fix_pr` arm that re-check turns a past-ceiling round
  //   into a `wait` — the #6323 repro (`apps/worker` `driver-stuck-rescue.test.ts`).
  // - `fix_pr` with a failed check rides the same clause but never NEEDS it: `aiManagedPr`
  //   gates every rescue and the managed CI ladder is unbounded (`managed ? Infinity`).
  //
  // A FEEDBACK action (`address_comments`) is the one rescue-marked kind NOT named, and
  // needs no arm: since #6344 `heldRefile` (`./stuck-rescue.ts`) marks it too, but only
  // by re-filing the park's own `behind.action`, and `parkBehindInFlight` below gates on
  // THIS ceiling first — a feedback action over its ladder is never parked, so never
  // re-filed. When the executor re-runs this predicate over the refiled round, the action
  // and the book are the very pair the park just judged UNDER the ceiling, so the general
  // branch below answers false exactly as it did there; the other feedback rescue
  // (`rescueUnrepeatable`, `apps/worker`) mints only after that same gate has passed.
  if ((action.kind === 'resolve_conflict' || action.kind === 'fix_pr') && action.rescue) {
    return false;
  }
  if (action.kind === 'resolve_conflict') {
    return book.conflictResolveAttempts >= policy.maxConflictResolveAttempts;
  }
  return attempt > attemptCeilingFor(action, policy, managed);
}

/** The round facts the detail names — the book's row, or the executor's fresh re-read. */
export type HeldRoundFacts = {
  readonly id: TaskId;
  readonly holdReason: TaskHoldReason | null;
  readonly ageMs: number;
};

/** The escalation's detail: the round, its hold (or that none was stamped), and how long. */
export function heldRoundDetail(
  round: HeldRoundFacts,
  reason: 'followup_held' | 'followup_unplaceable',
  action: BabysitAction['kind'],
): string {
  const hold = round.holdReason ? `(${round.holdReason})` : '(no hold stamped: no box took it)';
  const verb = reason === 'followup_held' ? 'held pending' : 'waiting with no machine to run it';
  return (
    `follow-up ${round.id} has been ${verb} ${hold} for ` +
    `${Math.round(round.ageMs / 60_000)} min — past the ${CI_WAIT_CEILING_MS / 60_000} min CI ceiling — ` +
    `so the ${action} this PR needs cannot run`
  );
}

/**
 * The held-round park (#6341): `decision` with its minting action replaced by
 * `escalate(followup_held | followup_unplaceable)` when the round it would mint
 * behind — `book.inFlightRound` — is still `pending` past {@link CI_WAIT_CEILING_MS}
 * and the decision is not past its own structural ceiling. Pure; returns `decision`
 * by identity when no park applies. See the module header for where this runs and
 * what defers to it.
 *
 * Which of the two is the hold's (#6231, `holdMeansNoMachine`): no hold at all, or
 * one that says no box can serve the row, is `followup_unplaceable` — nothing will
 * ever place it and a person is mailed. Any other hold has its own owner or clock
 * and stays the silent `followup_held`.
 */
export function parkBehindInFlight(
  decision: BabysitDecision,
  book: BabysitBookkeeping,
  policy: BabysitPolicy,
  managed: boolean,
): BabysitDecision {
  const action = decision.action;
  // Only a round-MINTING decision parks behind one: every other verdict (a wait, a
  // merge, an escalation) dispatches nothing a follow-up could block.
  if (
    action.kind !== 'fix_pr' &&
    action.kind !== 'resolve_conflict' &&
    action.kind !== 'address_comments'
  ) {
    return decision;
  }
  const round = book.inFlightRound;
  if (round === null) return decision;
  // The ceiling answers FIRST — the executor's order, kept: a decision past its own
  // ceiling keeps the `wait` the executor mints behind it (a rescue round is exempt
  // from its ceiling, not from this park).
  if (overAttemptCeiling(action, attemptFor(action, book), book, policy, managed)) {
    return decision;
  }
  // #4260: a follow-up still PENDING past the CI ceiling is not coming (ggrts#19: 747
  // hops over 7d 20h behind `repo_not_onboarded`). One claimed by a box, or younger
  // than the ceiling, may still run — the mint's guard dedupes behind it and waits.
  if (round.status !== TaskStatus.Pending || round.ageMs < CI_WAIT_CEILING_MS) return decision;
  const held = holdMeansNoMachine(round.holdReason) ? 'followup_unplaceable' : 'followup_held';
  // SPREAD the decision: `fault` and `membershipUnread` are the status comment's marks,
  // not the replaced mint's — the park swaps the action and the reason and adds
  // `behind`, carrying the action it replaced for the hop's executor.
  return {
    ...decision,
    action: { kind: 'escalate', reason: held },
    reason: heldRoundDetail(round, held, action.kind),
    behind: { round, held, action },
  };
}

/**
 * The CAPACITY holds (#7086): a held round waiting on key, provider or repo-env capacity,
 * which the platform frees by itself. Measured 7 days to 2026-10-24: 31 of 35
 * `followup_held` escalation rows (21 `key_saturated`, 7 `env_fault_exhausted`, 3
 * `provider_capacity`), 27 of the 30 PRs merged anyway. Such a park files NO escalation row:
 * the hop's `babysit.transition` row is its audit, the held TASK is the stalled-queue alert's
 * (`apps/worker` `cron/stalled-queue.ts`: `queueStallReport` names each member while its
 * window is live as its `TASK_HOLD_STALL` kind, pinned on the live DB by `@developerz/db`
 * `tasks/capacity-hold-stall.integration.test.ts`),
 * and the park still re-files when capacity frees (`refile_held`, `./stuck-rescue.ts`).
 * `key_reserved` and `pool_cooling` are the same walls (#6711's fold of `key_saturated`, and
 * the account-wide arm `provider_capacity` folds into).
 */
export const CAPACITY_HOLD_REASONS: readonly TaskHoldReason[] = [
  TaskHoldReason.KeySaturated,
  TaskHoldReason.KeyReserved,
  TaskHoldReason.ProviderCapacity,
  TaskHoldReason.PoolCooling,
  TaskHoldReason.EnvFaultExhausted,
];

/** Is a held round's hold a capacity wall ({@link CAPACITY_HOLD_REASONS})? */
export function isCapacityHold(holdReason: TaskHoldReason | null | undefined): boolean {
  return holdReason != null && CAPACITY_HOLD_REASONS.includes(holdReason);
}
